import {
  FulfillmentTypeGetResponseType,
  PriceTierOrderRule,
} from "@sellar-io/api-v2/types";
import {
  Button,
  DataButton,
  Form,
  H5,
  H6,
  Icon,
  InputCurrency,
  InputContainer,
} from "@sellar-io/components";
import { useState } from "react";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../hooks/usePriceFormat";
import { useForm, useStore } from "@tanstack/react-form";
import { OrderRuleDialogUpdate } from "../../orderRules/orderRule/components/OrderRuleDialogUpdate";
import { generatePriceTierOrderRuleDescription } from "../../../utils/sellarHelpers";
import { OrderRuleDialogCreate } from "../../orderRules/orderRule/components/OrderRuleDialogCreate";
import { useOrderRulesCreate } from "../../../hooks/api/v2/orderRules/useOrderRulesCreate";
import { useOrderRulesUpdate } from "../../../hooks/api/v2/orderRules/useOrderRulesUpdate";
import { z } from "zod";

export const DeliveryMethodOrderRules = ({
  deliveryMethod,
  readOnly = false,
}: {
  deliveryMethod?: FulfillmentTypeGetResponseType;
  readOnly?: boolean;
}) => {
  const [orderRuleDialogActive, setOrderRuleDialogActive] = useState(false);

  const orderRules = deliveryMethod?.OrderRules;

  const initialInputCharge = orderRules?.minimumOrderPrice
    ? formatPriceApiToInput(orderRules.minimumOrderPrice)
    : "";

  const formSchema = z.object({
    minimumBasketValue: z.string(),
  });

  const form = useForm({
    defaultValues: {
      minimumBasketValue: initialInputCharge,
    },
    onSubmit: async ({ value }) => {
      if (!deliveryMethod) return;

      const data = formSchema.parse(value);

      const minimumBasketValue =
        formatPriceInputToApi(data.minimumBasketValue) ?? 0;

      if (deliveryMethod.OrderRules?.id) {
        orderRulesUpdateMutation.mutate({
          minimumOrderPrice: minimumBasketValue,
        });
      } else {
        orderRulesCreateMutation.mutate({
          name: `${deliveryMethod.name ?? `Delivery Method ${deliveryMethod.id}`} - order rules`,
          minimumOrderPrice: minimumBasketValue,
          FulfillmentTypeId: deliveryMethod.id,
        });
      }
    },
  });

  const orderRulesCreateMutation = useOrderRulesCreate();
  const orderRulesUpdateMutation = useOrderRulesUpdate(
    deliveryMethod?.OrderRules?.id ?? 0,
  );

  const isLoading =
    orderRulesCreateMutation.isPending || orderRulesUpdateMutation.isPending;

  const minimumBasketValue = useStore(
    form.store,
    (state) => state.values.minimumBasketValue,
  );

  const minimumBasketValueUpdated = minimumBasketValue !== initialInputCharge;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <H5>Minimum basket value</H5>
          <H6>Leave this blank if you only want to set container rules</H6>
        </div>

        <Form
          className="flex gap-2"
          onSubmit={() => {
            form.handleSubmit();
          }}
        >
          <form.Field
            name="minimumBasketValue"
            validators={{
              onSubmit: ({ value }) => (value === "" ? "Required" : undefined),
            }}
          >
            {({ state, handleBlur, handleChange }) => (
              <InputContainer className="py-2">
                <InputCurrency
                  value={state.value}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleChange(value);
                  }}
                  onBlur={handleBlur}
                  disabled={readOnly}
                />
              </InputContainer>
            )}
          </form.Field>

          {!readOnly && (
            <DataButton
              border
              type="submit"
              disabled={!minimumBasketValueUpdated}
              loading={isLoading}
            >
              Save
            </DataButton>
          )}
        </Form>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <H5>Minimum order quantities</H5>
            <H6 className="max-w-xl">
              When setting multiple MOQ rules, only those which are relevant to
              the products in your customer's basket will apply.
            </H6>
          </div>

          {!readOnly && (
            <DataButton border onClick={() => setOrderRuleDialogActive(true)}>
              <Icon icon="PLUS" />
              Add
            </DataButton>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {orderRules?.PriceTierOrderRules.map((orderRule) => (
            <DeliveryMethodOrderRuleCard
              key={orderRule.id}
              orderRule={orderRule}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>

      <OrderRuleDialogCreate
        orderRulesId={orderRules?.id ?? null}
        active={orderRuleDialogActive}
        onDismiss={() => setOrderRuleDialogActive(false)}
        deliveryMethod={deliveryMethod}
        minimumOrderPrice={orderRules?.minimumOrderPrice}
      />
    </>
  );
};

const DeliveryMethodOrderRuleCard = ({
  orderRule,
  readOnly = false,
}: {
  orderRule: Pick<PriceTierOrderRule, "id" | "itemType" | "quantity">;
  readOnly?: boolean;
}) => {
  const [active, setActive] = useState(false);

  return (
    <>
      <div
        className="flex w-full cursor-pointer items-center justify-between rounded-lg bg-white px-4 py-2 hover:bg-zinc-50"
        onClick={() => setActive(!readOnly)}
      >
        <p>{generatePriceTierOrderRuleDescription(orderRule)}</p>
        {!readOnly && (
          <Button
            color="transparent"
            size="small"
            className="flex items-center gap-2"
          >
            Edit <Icon icon="CHEVRON_RIGHT" />
          </Button>
        )}
      </div>
      <OrderRuleDialogUpdate
        priceTierOrderRule={orderRule}
        active={active}
        onDismiss={() => setActive(false)}
      />
    </>
  );
};
